/**
 * The console's filter lock.
 *
 * Any dashboard board can LOCK the filters it is showing; every other board that
 * has those filters then shows them (marked, not editable) and applies them, so
 * the boards describe one slice of the book. Only the board that set the lock
 * releases it — the Unlock filters button in its sidebar — which is the point: no
 * board can quietly drop itself out of the comparison. See FunnelFilterDrawer
 * `applyLock`.
 *
 * The locking screen keeps editing while locked (it owns the lock), so its
 * changes travel straight through `updateLockedFilters`.
 *
 * Persisted with the list screens' filter state: sessionStorage, cleared on
 * logout — see PERSISTED_MODULES in store/index.js.
 */
const initialState = () => ({
  active: false,
  // Query params exactly as the locking screen built them, e.g.
  // { start_date, end_date, product_type: "Personal Loan,Business Loan", state: "..." }.
  params: {},
  // The sidebar's own picks behind those params (only the ones that narrow), so
  // another board can show them in its boxes, not just send them.
  values: {},
  // Which board locked them (route name), its title, and the filters in words,
  // for the notice the other boards show.
  owner: "",
  origin: "",
  summary: [],
});

export default {
  namespaced: true,
  state: initialState(),

  mutations: {
    LOCK(state, payload) {
      const data = payload || {};
      state.params = { ...(data.params || {}) };
      state.values = JSON.parse(JSON.stringify(data.values || {}));
      state.summary = (data.summary || []).slice();
      state.owner = data.owner || "";
      state.origin = data.origin || "";
      state.active = true;
    },
    UPDATE(state, payload) {
      if (!state.active) return;
      const data = payload || {};
      state.params = { ...(data.params || {}) };
      state.values = JSON.parse(JSON.stringify(data.values || {}));
      state.summary = (data.summary || []).slice();
    },
    UNLOCK(state) {
      Object.assign(state, initialState());
    },
  },

  actions: {
    lockFilters({ commit }, payload) {
      commit("LOCK", payload);
    },
    updateLockedFilters({ commit }, payload) {
      commit("UPDATE", payload);
    },
    unlockFilters({ commit }) {
      commit("UNLOCK");
    },
  },

  getters: {
    isLocked: (state) => state.active,
    lockedParams: (state) => state.params,
    lockSummary: (state) => state.summary,
    lockOrigin: (state) => state.origin,
    lockOwner: (state) => state.owner,
    lockValues: (state) => state.values || {},
    /** Only the params a screen actually supports, so a lock cannot send a filter
     *  an endpoint would ignore or choke on. */
    lockedSubset: (state) => (keys) => {
      const out = {};
      if (!state.active) return out;
      (keys || []).forEach((key) => {
        if (state.params[key] !== undefined && state.params[key] !== null) {
          out[key] = state.params[key];
        }
      });
      return out;
    },
  },
};
